import React, { useEffect, useRef, useState } from 'react';

import { useQuery } from '@apollo/client';
import loadable from '@loadable/component';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { Bullets, FixedBtns, Highlights, PriceComponent, Typography, ViewMore } from '@quikr/ui';
import TopFeature from '@quikr/ui/src/common/TopFeature';
import SlideTransition from '@quikr/ui/src/common/Transitions/SlideTransition';
import { priceChecker } from '../../../../../../components/src/helpers/common';
import { BAZAAR_CATS } from '../../../constants';
import { adsBelowCta } from '../../../googleAdConfig/core';
import { common } from '../../../gql/common';
import { getCTAHighlights } from '../../../helpers/utils';
import { useGa } from '../../../hooks';

const GoogleAd = loadable(() => import('@quikr/ui/src/common/GoogleAd'), { ssr: false });

const GoogleAdSec = styled(GoogleAd)`
  padding: 30px 40px 0px 0px;
`;

const VapTitle = styled(Typography)`
  color: ${({ theme }) => theme.colors.TextAlpha};
  margin: 16px 0 12px 0;
  &::first-letter {
    text-transform: uppercase;
  }
`;

const Li = styled(Typography)`
  color: ${({ theme }) => theme.colors.TextBeta};
  &::before {
    content: '|';
    color: ${({ theme }) => theme.colors.Neutral[100]};
    margin: 0 5px;
  }
  &:first-child {
    max-width: 34%;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    &:before {
      display: none;
    }
  }
  @media screen and (min-width: 768px) {
    &::before {
      margin: 0 10px;
    }
  }
`;

const Wrapper = styled.div`
  @media (max-width: 767px) {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-top: 15px;
  }
`;

const PriceWrapper = styled(PriceComponent)`
  @media screen and (max-width: 767px) {
    margin-top: 12px;
  }
`;

const Sticky = styled.div`
  ${({ mobile, top }) =>
  !mobile &&
    css`
      position: sticky;
      top: ${top}px;
      z-index: 1;
    `};
`;

const Link = styled.a`
  color: ${({ theme }) => theme.colors.TextBeta};
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.colors.Primary};
    text-decoration: underline;
  }
`;

const BoldLink = styled(Link)`
  color: ${({ theme }) => theme.colors.TextAlpha};
  margin: 0px 5px;
`;

const SecondSubtile = styled(Typography)`
  color: ${({ theme }) => theme.colors.TextBeta};
  margin-top: 8px;
`;

const VapInfo = props => {
  const compRef = useRef();
  const {
    highlights,
    title,
    alttitle,
    price,
    locality,
    views,
    timeago,
    actions,
    cat,
    subcat,
    city,
    features = [],
    builderdetails = {},
    projectdetails = {},
    propertytype
  } = props.data;

  const { loading, data } = useQuery(common);
  const { user, abRandValue } = data;
  const { TriggerGaEvent } = useGa();
  const [ctaToShow, setctaToShow] = useState(false);
  const ctaHighlights = getCTAHighlights(cat.id);

  const userid = user && user.user && user.user.id;

  const checkPos = (disconnect = false) => {
    if (typeof IntersectionObserver !== 'undefined') {
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              setctaToShow(false);
            } else {
              setctaToShow(true);
            }
          });
        },
        {
          threshold: 0.9
        }
      );
      compRef.current && observer.observe(compRef.current);
      if (disconnect) {
        observer.disconnect();
      }
    }
  };

  useEffect(() => {
    if (data.isMobile) {
      checkPos();
    }
    return () => checkPos(true);
  }, [data]);

  const tabClick = id => {
    document.getElementById(id).click();
  };

  const actionButtonClickEvent = eventLabel => {
    TriggerGaEvent({
      category: 'VAP',
      label: eventLabel,
      cityName: city.name,
      catName: cat.name,
      subcatName: subcat.name
    });
  };

  const handleActionClick = action => {
    switch (action) {
      case 'CONTACT_BROKER':
      case 'CONTACT_BUILDER':
      case 'CONTACT_OWNER':
      case 'CALL':
        if (props.tempRedirect) {
          props.tempRedirect('call');
          break;
        }
        if (props.redirectToQApp) {
          props.redirectToQApp('CALL');
          break;
        }
        props.openCall();
        ctaToShow
          ? actionButtonClickEvent('stickyCtaSection_call_init')
          : actionButtonClickEvent('topCtaSection_call_init');
        break;
      case 'CHAT':
        if (props.tempRedirect) {
          props.tempRedirect('chat');
          break;
        }
        if (props.redirectToQApp) {
          props.redirectToQApp('CHAT');
          break;
        }
        props.openChat();
        ctaToShow
          ? actionButtonClickEvent('stickyCtaSection_chat_init')
          : actionButtonClickEvent('topCtaSection_chat_init');
        break;
      case 'WHATSAPP':
        if (props.tempRedirect) {
          props.tempRedirect('whatsapp');
          break;
        }
        if (props.redirectToQApp) {
          props.redirectToQApp('WHATSAPP');
          break;
        }
        props.openWhatsapp();
        ctaToShow
          ? actionButtonClickEvent('stickyCtaSection_whatsapp_init')
          : actionButtonClickEvent('topCtaSection_whatsapp_init');
        break;
      default:
        break;
    }
  };

  if (loading) return null;
  const pricyCats = ['60', '20', '269', '40', '247'];
  const askPrice = !price && pricyCats.includes(cat.id);
  const finalPrice = priceChecker(price);
  let localityLink = '';
  if (locality && locality.name && city.name) {
    localityLink =
      '/homes/locality/' +
      (locality.name.toLowerCase() + '-' + city.name.toLowerCase()).split(' ').join('_') +
      '/' +
      String(locality.id);
  }
  return (
    <Sticky
      className={`vap-right-col ${(cat.id !== '60' || cat.id !== '20') && 'mob-border'}`}
      id="stickyBtnsLeft"
      mobile={data.isMobile}
      top={cat.id === '20' && !data.isMobile ? 156 : 140}
    >
      {highlights && highlights.length > 0 && <Bullets data={highlights} />}
      <VapTitle tagName="h1" modifiers="h1">
        <ViewMore content={alttitle || title} maxLength={data.isMobile ? 68 : 133} />
      </VapTitle>
      <ul className="dF fwW">
        {locality && locality.name && (
          <Li tagName="li" modifiers="bt2" title={locality.name}>
            {cat.id === '20' && localityLink ? (
              <Link href={localityLink} target="_blank">
                {locality.name}
                {', ' + city.name}
              </Link>
            ) : (
              <>{locality.name}</>
            )}
          </Li>
        )}
        {cat.id && cat.id !== '20' && BAZAAR_CATS.indexOf(parseInt(cat.id)) > -1
          ? city &&
            city.name && (
            <Li tagName="li" modifiers="bt2" title={city.name}>
              {city.name}
            </Li>
          )
          : views && (
            <Li tagName="li" modifiers="bt2">
              {views + ' views'}
            </Li>
          )}
        {timeago && (
          <Li tagName="li" modifiers="bt2">
            {'Posted ' + timeago}
          </Li>
        )}
      </ul>
      {cat.id === '20' && projectdetails && (
        <SecondSubtile tagName="p" modifiers={['bt2', 'medium']} className="dF fwW">
          {propertytype} in
          <BoldLink onClick={() => tabClick(3)}>{' ' + projectdetails.name} </BoldLink>
          {builderdetails && (
            <>
              <>by</> <BoldLink onClick={() => tabClick(4)}>{' ' + builderdetails.name} </BoldLink>
            </>
          )}
        </SecondSubtile>
      )}
      <Wrapper>
        <PriceWrapper ref={compRef} price={finalPrice} askForPrice={askPrice} />
        <FixedBtns type="top" actions={actions.top} actionClick={handleActionClick} />
        <SlideTransition in={ctaToShow} duration={500} appear>
          <FixedBtns type="bottom" actions={actions.bottom} actionClick={handleActionClick} />
        </SlideTransition>
      </Wrapper>
      {data.isMobile && cat.id === '20' && <TopFeature features={features} />}
      <Highlights data={ctaHighlights} />
      <GoogleAdSec className="taC" config={adsBelowCta(cat.name)} />
    </Sticky>
  );
};

VapInfo.propTypes = {
  data: PropTypes.object,
  openCall: PropTypes.func,
  openChat: PropTypes.func,
  openWhatsapp: PropTypes.func,
  redirectToQApp: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  tempRedirect: PropTypes.oneOfType([PropTypes.bool, PropTypes.func])
};

VapInfo.defaultProps = {};

const areEqual = (prevProps, nextProps) => {
  return prevProps.data.id === nextProps.data.id;
};

export default React.memo(VapInfo, areEqual);
